import { HttpException, UnauthorizedException } from '../http';
import { deleteStudy, uploadDicomFile } from '../clients/viewer';
import { describeError } from './describeError';
import { ensurePreamble } from './dicomIntake';
import { UploadStatus } from './uploadTypes';

/**
 * D3. ONE DROP = ONE STUDY. Every behaviour below is only correct under that
 * invariant, and it is stated here because nothing on develop records it — which is
 * why develop's two most surprising behaviours read as defects rather than as
 * policy:
 *
 *   - Continue forces EVERY remaining file (useDicomUpload.ts:162-226).
 *   - Stop deletes EVERY study UID accumulated in the session (:229-246).
 *
 * Under "a drop may hold several studies" both are over-broad. Under one-study they
 * are exactly right: accept the mismatch FOR THIS STUDY, and abandon THIS STUDY.
 * develop's own drop-zone caption agrees, in the singular — "Max total size of
 * unzipped study".
 *
 * Sequential execution is a CORRECTNESS CONSTRAINT, not a preference (§5.4):
 * halt-and-confirm requires that no further files land while the user decides, so
 * that Stop can roll back a known set. Upstream uploads all files in parallel
 * (DicomUploadProgress.tsx:126-209 calls .load() on every uploader in one effect),
 * which is the structural reason its progress component could not be reused
 * whatever its export surface.
 *
 * §5.1: nothing in this file imports React, and nothing imports a UI service.
 */

export interface BatchCallbacks {
  onFileStarted: (index: number) => void;
  onFileProgress: (index: number, percent: number) => void;
  onFileSettled: (index: number, status: UploadStatus, error?: string) => void;
}

/**
 * `rejected` and `failed` differ in ONE thing: whether resending with
 * x-skip-validation could plausibly help. A 4xx is the backend judging the file,
 * so forcing past it is the offer §5.4's Continue exists to make; a status 0 (no
 * response at all — exceptions.ts:12-17) or a 5xx is the transport or the server
 * itself faulting, and re-sending the whole remaining study with validation
 * disabled is not a remedy for either. The caller renders one exit or two off
 * this distinction.
 */
export type BatchOutcome =
  | { type: 'completed' }
  | { type: 'rejected'; index: number; message: string }
  | { type: 'failed'; index: number; message: string }
  | { type: 'cancelled' }
  | { type: 'unauthorized' };

export interface BatchOptions {
  /** §5.4: Continue resumes with force: true -> x-skip-validation: 'true'. */
  force: boolean;
  signal: AbortSignal;
  /** Accumulates across a halt and a resume, so Stop can roll the whole run back. */
  studyUids: Set<string>;
  callbacks: BatchCallbacks;
}

/**
 * §6 defect 4. develop's processFiles (:248-300) and continueWithForce (:162-226)
 * are near-identical ~60-line loops differing only in a `force: true` and a
 * preamble that resets the first remaining file. One loop, one flag.
 *
 * `indices` rather than a sliced array: the caller resumes "from the first
 * non-Success file" (§5.4) and the row indices must survive that, because they are
 * what every callback addresses.
 */
export async function runUploadBatch(
  files: readonly File[],
  indices: readonly number[],
  { force, signal, studyUids, callbacks }: BatchOptions
): Promise<BatchOutcome> {
  for (const index of indices) {
    if (signal.aborted) {
      callbacks.onFileSettled(index, UploadStatus.Cancelled);
      return { type: 'cancelled' };
    }

    callbacks.onFileStarted(index);

    const file = files[index];

    try {
      // Immediately before upload, not eagerly at intake (§5.2): preparing every
      // file up front would hold N Blob copies against a 1 GiB cap.
      const prepared = await ensurePreamble(file);

      const studyInstanceUID = await uploadDicomFile(prepared, {
        force,
        signal,
        onProgress: (loaded: number, total: number) =>
          callbacks.onFileProgress(
            index,
            // `total || file.size || 1`: axios reports 0 when it cannot determine
            // the request length, and a zero divisor would emit NaN into the row.
            Math.min(100, Math.round((loaded * 100) / (total || file.size || 1)))
          ),
      });

      studyUids.add(studyInstanceUID);
      callbacks.onFileSettled(index, UploadStatus.Success);
    } catch (error) {
      /**
       * Plan D-14. Tested on the CONTROLLER, not on the error. axios reports an
       * abort as CanceledError with no `response`, which HttpClient.toException
       * maps to HttpException(0, 'canceled') — identical in shape to a dropped
       * connection or a DNS failure. develop discriminated on
       * `error?.message?.includes('cancelled')` (:211, :280), which is §6 defect 7.
       */
      if (signal.aborted) {
        callbacks.onFileSettled(index, UploadStatus.Cancelled);
        return { type: 'cancelled' };
      }

      /**
       * D8. A 401 aborts the batch and does NOT open Continue/Stop: offering to
       * force past a dead session is meaningless, and the rollback DELETE would 401
       * too. Discriminated BEFORE the rejection branch, or a dead session reads as
       * a validation rejection. develop could not make this distinction at all.
       */
      if (error instanceof UnauthorizedException) {
        callbacks.onFileSettled(index, UploadStatus.Failed, error.message);
        return { type: 'unauthorized' };
      }

      const message = error instanceof Error ? error.message : 'Upload failed';

      callbacks.onFileSettled(index, UploadStatus.Failed, message);

      // 401 already returned above, so any 4xx reaching here is a content
      // judgement. Everything else — status 0, 5xx, and uploadDicomFile's own
      // "answered N with no id.studyInstanceUID" (clients/viewer.ts, which carries
      // the SUCCESS status) — is not something a force can answer.
      const isContentRejection =
        error instanceof HttpException && error.status >= 400 && error.status < 500;

      return { type: isContentRejection ? 'rejected' : 'failed', index, message };
    }
  }

  return { type: 'completed' };
}

/**
 * §5.4's Stop. Returns the UIDs it could NOT delete, because a failed rollback
 * leaves orphaned data in the PACS and the user needs to know — develop swallowed
 * these to console.error (:243), which is §6 defect 14. The caller raises the
 * notification; §5.1 keeps that decision out of here.
 *
 * Sequential, matching the upload loop: a parallel DELETE storm against a backend
 * that just rejected something is not obviously safe, and this path is at most a
 * few hundred requests once per halted run.
 */
export async function rollbackStudies(studyUids: Iterable<string>): Promise<string[]> {
  const failed: string[] = [];

  for (const studyInstanceUid of studyUids) {
    try {
      await deleteStudy(studyInstanceUid);
    } catch (error) {
      failed.push(studyInstanceUid);
      console.error(
        `[sycai] rollback DELETE failed for ${studyInstanceUid} ${describeError(error)}`
      );
    }
  }

  return failed;
}
